/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import {
  DEFAULT_PICKER_COLOR,
  PICKER_COLORS,
  SUPPORTED_IMAGE_EXTENSIONS,
  SUPPORTED_VIDEO_EXTENSIONS
} from '~backoffice/app/~shared/constants/global.constants';
import { SnackbarService } from '~backoffice/app/~shared/services/snackbar.service';
import { ProgramService } from '~core/services/program.service';
import { GlobalFacade } from '~backoffice/app/~store/facades/global.facade';
import { Subject, finalize, takeUntil, tap } from 'rxjs';
import { Program, ProgramCreationParams } from '~core/models/program.models';
import {
  httpErrorToFormGroupError,
  setErrorToControls
} from '@veone/ngx-utilities/form';

@Component({
  standalone: false,
  selector: 'edit-program-banner',
  templateUrl: './edit-program-banner.component.html',
  styleUrls: ['./edit-program-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditProgramBannerComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() program: Program | null | undefined;
  isLoading = false;

  form = new FormGroup({
    bannerTitle: new FormControl<string | undefined>('', [
      Validators.required,
      Validators.minLength(3)
    ]),
    bannerTitleColor: new FormControl<string | undefined>(''),
    bannerDescription: new FormControl<string | undefined>('', [
      Validators.required,
      Validators.minLength(3)
    ]),
    bannerDescriptionColor: new FormControl<string | undefined>('', [
      Validators.minLength(3)
    ])
  });

  bannerControl = new FormControl<File | null>(null);

  supportedImageExtensions = SUPPORTED_IMAGE_EXTENSIONS;
  supportedVideoExtensions = SUPPORTED_VIDEO_EXTENSIONS;

  colors = PICKER_COLORS;
  defaultColor = DEFAULT_PICKER_COLOR;

  violations$ = this.globalFacade.formViolations$.pipe(
    tap({
      next: violations => {
        setTimeout(() => {
          violations?.forEach(violation => {
            if (violation.propertyPath.includes('file')) {
              this.bannerControl.setErrors({
                httpError: violation.title
              });
              return;
            }
            this.form.get(violation.propertyPath)?.setErrors({
              httpError: violation.title
            });
          });
          this.cdRef.detectChanges();
        }, 500);
      }
    })
  );

  private _destroy$ = new Subject<void>();
  constructor(
    private globalFacade: GlobalFacade,
    private programService: ProgramService,
    private snackbarService: SnackbarService,
    private dialogRef: MatDialogRef<EditProgramBannerComponent>,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.patchFormValues(this.program);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.patchFormValues(changes['program']?.currentValue);
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }

  patchFormValues(program: Program | null | undefined): void {
    if (program) {
      this.form.patchValue({
        bannerTitle: program.banner?.title.value ?? '',
        bannerTitleColor: program.banner?.title.color ?? '',
        bannerDescription: program.banner?.description?.value ?? '',
        bannerDescriptionColor: program.banner?.description?.color ?? ''
      });
    }
  }

  setColor(
    color: string,
    property: 'bannerTitleColor' | 'bannerDescriptionColor' = 'bannerTitleColor'
  ): void {
    this.form.patchValue({ [property]: color });
  }

  update(): void {
    if (!this.program || this.form.invalid) return;
    this.isLoading = true;
    const values = this.form.value;
    const data = {
      banner: {
        title: {
          value: values.bannerTitle,
          ...(values.bannerTitleColor && { color: values.bannerTitleColor })
        },
        description: {
          value: values.bannerDescription,
          ...(values.bannerDescriptionColor && {
            color: values.bannerDescriptionColor
          })
        },
        ...(this.bannerControl.value && {
          file: this.bannerControl.value
        })
      }
    };

    this.programService
      .update(this.program.id, data as unknown as ProgramCreationParams)
      .pipe(
        takeUntil(this._destroy$),
        tap({
          next: program => {
            this.dialogRef.close(program);
            this.snackbarService.open({
              text: `Données de bannière mises à jour`,
              type: 'success'
            });
          },
          error: error => {
            const formGroupError = httpErrorToFormGroupError(error);
            const bannerError = formGroupError.find(el =>
              el.propertyPath.includes('file')
            );
            if (bannerError) {
              this.bannerControl.setErrors({
                httpError: bannerError.title
              });
              return;
            }
            const bannerTitleError = formGroupError.find(
              el =>
                el.propertyPath.includes('banner') &&
                el.propertyPath.includes('title')
            );
            if (bannerTitleError) {
              this.form.get('bannerTitle')?.setErrors({
                httpErrors: bannerTitleError.title
              });
            }
            const bannerDescriptionError = formGroupError.find(
              el =>
                el.propertyPath.includes('banner') &&
                el.propertyPath.includes('description')
            );
            if (bannerDescriptionError) {
              this.form.get('bannerDescription')?.setErrors({
                httpErrors: bannerDescriptionError.title
              });
            }
            setErrorToControls(this.form, formGroupError);
          }
        }),
        finalize(() => {
          this.isLoading = false;
          this.cdRef.detectChanges();
        })
      )
      .subscribe();
  }

  setFile(files: File[] | null): void {
    this.bannerControl.patchValue(files ? files[0] : null);
    this.resetFormErrors();
  }

  resetFormErrors(): void {
    const keys = Object.keys(this.form.controls);
    keys.forEach(key => {
      this.form.get(key)?.setErrors(null);
      this.form.get(key)?.updateValueAndValidity();
    });
    this.cdRef.detectChanges();
  }

  get modalTitle(): string {
    return 'Configuration de la bannière du programme';
  }

  get modalDescription(): string {
    return 'Veuillez remplir les champs ci-dessous pour la bannière du programme';
  }

  get isFormValid(): boolean {
    return this.program?.banner
      ? this.form.valid
      : this.form.valid && !!this.bannerControl.value;
  }
}
